import React, {
  useCallback, useEffect, useState,
} from 'react';
import {
  Check, CircleDashed, FileText, FolderOpen, Globe2, Hourglass, Loader2, Mail, SlidersHorizontal, X,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import {
  decideValidation, loadBookingEligibility, loadReportOptions, loadReportRequest, loadValidationPeople,
} from './api';
import { tx } from './i18n';
import { ownSampleTypeName } from './LaboratoryText';
import { FilterMenu, ago, useNow, when } from './MobileParts';
import {
  Avatar, Empty, Failed, Head, Loading, Pill,
} from './parts';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { RequestDetailModal } from './XomicsReports';
import './validation.css';

const PAGE_SIZE = 25;
const STATUSES = ['pending', 'waiting', 'approved', 'rejected', 'all'];
const TONES = { waiting: 'info', pending: 'warn', approved: 'ok', rejected: 'bad' };
const OUTPUT_ICONS = { done: Check, running: Loader2, failed: X, missing: CircleDashed };
const EMAIL_RE = /^\S+@\S+\.\S+$/;
const text = (key, values) => tx(`ui.validation.${key}`, values);

// The pipeline outputs one review covers: a kit's finished pipeline, or every
// pipeline a general collection asked for with how far each got.
export function reviewOutputs(row) {
  return row.progress ? row.progress.pipelines.map((pipeline) => ({ key: pipeline.id, name: pipeline.name, state: pipeline.state }))
    : [{ key: row.latestJob.id, name: row.latestJob.pipelineName, state: 'done' }];
}

// Who a person is on this page, and where they come from. Without the right to
// read identities the person stays unnamed.
export function personName(patient) {
  return patient.name || (patient.id ? `#${patient.id}` : text('patient'));
}

export function projectLine(person) {
  if (person.project === null) return '';
  const group = person.project.group === null ? null : person.project.group.name || person.project.group.code;
  return group ? `${group} · ${person.project.name}` : person.project.name;
}

function emailPill(status) {
  return <Pill tone={status === 'SENT' ? 'ok' : status === 'FAILED' ? 'bad' : 'flat'}>{text(`email_${status.toLowerCase()}`)}</Pill>;
}

function Dialog({ title, detail, busy, onCancel, children, footer }) {
  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [busy, onCancel]);
  return (
    <div className="xo-val-dialog-backdrop" role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
      <section className="xo-val-dialog" role="dialog" aria-modal="true" aria-labelledby="xo-val-dialog-title">
        <header>
          <div><h2 id="xo-val-dialog-title">{title}</h2><p>{detail}</p></div>
          <button type="button" className="xo-icon-btn" aria-label={text('cancel')} disabled={busy} onClick={onCancel}><X aria-hidden="true" /></button>
        </header>
        <div className="xo-val-dialog-body">{children}</div>
        <footer>{footer}</footer>
      </section>
    </div>
  );
}

// What approving sends, chosen before it goes: the patient's email on top, then
// the emails. A kit always sends its report email; a general collection (no
// product) its results email unless left out, which also invites an email
// without an app account. The booking follow-up is ticked for general
// collections and left for kits.
function ApproveDialog({ row, detail, busy, onCancel, onApprove }) {
  const isKit = row.collection.product !== null;
  const [sendResults, setSendResults] = useState(true);
  const [sendBooking, setSendBooking] = useState(false);
  // The booking follow-up books a video consultation with the validator, so
  // it is offered only when their email is a clinic portal account.
  const [booking, setBooking] = useState({ status: 'loading' });
  const email = row.patient.email || '';
  const hasEmail = EMAIL_RE.test(email);

  useEffect(() => {
    let current = true;
    loadBookingEligibility().then((answer) => {
      if (!current) return;
      setBooking({ status: 'ready', ...answer });
      setSendBooking(Boolean(answer.eligible) && !isKit);
    }).catch(() => { if (current) setBooking({ status: 'ready', eligible: false, reason: 'CLINIC_PORTAL_UNAVAILABLE' }); });
    return () => { current = false; };
  }, [isKit]);
  const bookingNote = booking.status === 'loading' ? text('booking_checking')
    : booking.eligible ? text('booking_with', { name: booking.clinician?.name || '' })
      : booking.reason === 'CLINIC_PORTAL_UNAVAILABLE' ? text('booking_portal_down')
        : text('booking_not_clinician');

  return (
    <Dialog title={text('approve_results')} detail={detail} busy={busy} onCancel={onCancel} footer={<>
      <button type="button" className="xo-btn-2" disabled={busy} onClick={onCancel}>{text('cancel')}</button>
      <button type="button" className="xo-btn" disabled={busy || !hasEmail}
        onClick={() => onApprove({ sendResults: isKit ? true : sendResults, sendBooking })}><Check aria-hidden="true" />{text('approve')}</button>
    </>}>
      <div className={`xo-val-email${hasEmail ? '' : ' is-missing'}`}>
        <span>{text('patient_email')}</span>
        <strong>{hasEmail ? email : text('email_missing')}</strong>
      </div>
      <label className="xo-val-check">
        <input type="checkbox" checked={isKit || sendResults} disabled={isKit || busy || !hasEmail}
          onChange={(event) => setSendResults(event.target.checked)} />
        <span>
          <strong>{isKit ? text('send_report') : text('send_results')}</strong>
          <small>{isKit ? text('send_report_note') : text('send_results_note')}</small>
        </span>
      </label>
      <label className={`xo-val-check${booking.eligible ? '' : ' is-unavailable'}`}>
        <input type="checkbox" checked={sendBooking} disabled={busy || !hasEmail || !booking.eligible}
          onChange={(event) => setSendBooking(event.target.checked)} />
        <span>
          <strong>{text('send_booking_video')}</strong>
          <small>{bookingNote}</small>
        </span>
      </label>
    </Dialog>
  );
}

// A customer's sample (sent through their portal) has no patient to email:
// approving it releases its patient reports to the customer there, and tells
// the customer's people by email unless left out.
function ReleaseDialog({ detail, busy, onCancel, onApprove }) {
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  return (
    <Dialog title={text('approve_results')} detail={detail} busy={busy} onCancel={onCancel} footer={<>
      <button type="button" className="xo-btn-2" disabled={busy} onClick={onCancel}>{text('cancel')}</button>
      <button type="button" className="xo-btn" disabled={busy}
        onClick={() => onApprove({ sendResults: false, sendBooking: false, notifyCustomer })}><Check aria-hidden="true" />{text('approve')}</button>
    </>}>
      <p className="xo-val-customer-note"><FileText aria-hidden="true" /><span>{text('customer_sample_release_note')}</span></p>
      <label className="xo-val-check">
        <input type="checkbox" checked={notifyCustomer} disabled={busy}
          onChange={(event) => setNotifyCustomer(event.target.checked)} />
        <span>
          <strong>{text('notify_customer')}</strong>
          <small>{text('notify_customer_note')}</small>
        </span>
      </label>
    </Dialog>
  );
}

function RejectDialog({ detail, busy, onCancel, onReject }) {
  const [note, setNote] = useState('');
  return (
    <Dialog title={text('reject_results')} detail={detail} busy={busy} onCancel={onCancel} footer={<>
      <button type="button" className="xo-btn-2" disabled={busy} onClick={onCancel}>{text('cancel')}</button>
      <button type="button" className="xo-btn xo-val-reject" disabled={busy || note.trim() === ''} onClick={() => onReject(note.trim())}>
        <X aria-hidden="true" />{text('reject')}</button>
    </>}>
      <label className="xo-val-reason"><span>{text('reject_reason')}</span>
        <textarea value={note} maxLength={1000} rows={4} autoFocus disabled={busy} onChange={(event) => setNote(event.target.value)} /></label>
    </Dialog>
  );
}

// Whether a review's reports let it be decided, by the server's rule (Validation.js reportGate): nothing while one
// is still being made, and no approval with one that failed. Results with no report are decided as before.
export function reportGate(reports) {
  if (reports.some((report) => report.status === 'queued' || report.status === 'processing')) return 'making';
  if (reports.some((report) => report.status === 'failed' || report.status === 'cancelled')) return 'failed';
  return 'ready';
}

// One review: the outputs it covers, what was sampled and where, and the decision.
function Review({ row, now, busy, onApprove, onReject, onOpenReport }) {
  const outputs = reviewOutputs(row);
  const left = row.progress ? row.progress.total - row.progress.done : 0;
  const gate = reportGate(row.reports ?? []);
  const place = row.origin !== null ? row.origin.name : row.clinic !== null ? row.clinic.name : null;
  const what = row.collection.product !== null ? row.collection.product.name : row.sampleType ? ownSampleTypeName(row.sampleType) : null;
  return (
    <li className={`xo-val-review is-${row.status}`}>
      <div className="xo-val-review-main">
        <ul className="xo-val-outputs" aria-label={text('outputs')}>{outputs.map((output) => {
          const Icon = OUTPUT_ICONS[output.state];
          return <li key={output.key} className={`is-${output.state}`} title={text(`pipeline_${output.state}`)}>
            <span><Icon aria-hidden="true" /></span>{output.name}</li>;
        })}</ul>
        {(row.reports ?? []).length > 0 && <div className="xo-val-reports">{row.reports.map((report) => (
          <button key={report.uuid} type="button" onClick={() => onOpenReport(report)}>
            <FileText aria-hidden="true" />{report.name}
          </button>
        ))}</div>}
        <p className="xo-val-review-meta">{[what, place, row.customerSample ? text('customer_sample') : null].filter(Boolean).join(' · ')}
          <span title={when(row.updatedAt)}>{text('ready_ago', { when: ago(row.updatedAt, now) })}</span></p>
        {row.decidedBy && <p className="xo-val-review-meta">{row.decidedBy.name}<span title={when(row.decidedAt)}>{ago(row.decidedAt, now)}</span></p>}
        {row.note && <blockquote>{row.note}</blockquote>}
        {(row.patientEmailStatus !== null || row.bookingEmailStatus !== null) && <p className="xo-val-emails">
          {row.patientEmailStatus !== null && <span><Mail aria-hidden="true" />{text('patient_email')} {emailPill(row.patientEmailStatus)}</span>}
          {row.bookingEmailStatus !== null && <span>{text('booking_email')} {emailPill(row.bookingEmailStatus)}</span>}
        </p>}
      </div>
      <div className="xo-val-review-side">
        <Pill tone={TONES[row.status] || 'flat'}>{row.status === 'waiting' && <Hourglass aria-hidden="true" />}{text(`status_${row.status}`)}</Pill>
        {row.status === 'waiting' && <small>{text('waiting_count', { count: left })}</small>}
        {row.status === 'pending' && gate !== 'ready' && <small>{text(gate === 'making' ? 'report_making' : 'report_failed')}</small>}
        {row.status === 'pending' && <div className="xo-val-actions">
          <button type="button" className="xo-btn-2" disabled={busy || gate === 'making'} onClick={onReject}><X aria-hidden="true" />{text('reject')}</button>
          <button type="button" className="xo-btn" disabled={busy || gate !== 'ready'} onClick={onApprove}><Check aria-hidden="true" />{text('approve')}</button>
        </div>}
      </div>
    </li>
  );
}

function Person({ person, now, busy, onApprove, onReject, onOpenReport }) {
  const name = personName(person.patient);
  const pending = person.validations.filter((row) => row.status === 'pending').length;
  const project = projectLine(person);
  return (
    <article className="xo-val-person" aria-label={name}>
      <header>
        <Avatar name={name} />
        <div className="xo-val-person-name"><strong>{name}</strong>
          {project !== '' && <span><FolderOpen aria-hidden="true" />{project}</span>}</div>
        {person.patient.email && <span className="xo-val-person-email"><Mail aria-hidden="true" />{person.patient.email}</span>}
        {pending > 0 && <Pill tone="warn">{text(pending === 1 ? 'to_review_one' : 'to_review', { count: pending })}</Pill>}
      </header>
      <ul className="xo-val-reviews">{person.validations.map((row) => <Review key={row.collection.uuid} row={row} now={now}
        busy={busy === row.collection.uuid} onApprove={() => onApprove(person, row)} onReject={() => onReject(person, row)}
        onOpenReport={onOpenReport} />)}</ul>
    </article>
  );
}

export default function XomicsValidation() {
  const { permissions } = useXomicsAuth();
  const mayGlobal = permissions.includes('SAMPLE_VALIDATE_GLOBAL');
  const [scope, setScope] = useState('local');
  const [status, setStatus] = useState('pending');
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // The review being decided, with the person it belongs to.
  const [deciding, setDeciding] = useState(null);
  // The report open in the same window the Reports page uses.
  const [openReport, setOpenReport] = useState(null);
  const now = useNow(30000);

  useEffect(() => {
    if (!mayGlobal && scope !== 'local') setScope('local');
  }, [mayGlobal, scope]);

  const load = useCallback(() => loadValidationPeople({ scope, status, limit: PAGE_SIZE, offset }), [offset, scope, status]);
  const resource = useLiveResource(['validations'], load);
  const data = resource.data;
  const current = data !== null && data.scope === scope && data.status === status && data.offset === offset;
  const change = (setter) => (value) => { setter(value); setOffset(0); };
  const ask = (decision) => (person, row) => { setError(''); setNotice(''); setDeciding({ decision, person, row }); };

  async function showReport(report) {
    setError('');
    try {
      const [request, options] = await Promise.all([loadReportRequest(report.uuid), loadReportOptions()]);
      setOpenReport({ request, options });
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  async function decide(row, decision, note, emails = {}) {
    setBusy(row.collection.uuid);
    setError('');
    setNotice('');
    try {
      const result = await decideValidation(row.collection.uuid, decision, row.revision, note, emails);
      setDeciding(null);
      await resource.refresh();
      if (decision !== 'approved') {
        setNotice(text('results_rejected'));
      } else if (row.customerSample) {
        // Whether the customer was emailed (null: not asked to, or they wanted no reports).
        const told = result?.customerTold ?? null;
        setNotice([
          text('results_approved_customer'),
          told === null ? null : !told.failed && told.emailed.length > 0 ? text('customer_emailed') : text('customer_not_emailed'),
        ].filter(Boolean).join(' '));
      } else {
        const invite = result?.invite ?? null;
        setNotice([
          text('results_approved'),
          invite === null ? null : text(`invite_${invite.outcome}`, { code: invite.code || '' }),
        ].filter(Boolean).join(' '));
      }
    } catch (requestError) {
      const errorKey = {
        PATIENT_EMAIL_MISSING: 'ui.validation.email_missing',
        PRODUCT_REPORT_MAPPING_MISSING: 'ui.validation.product_report_missing',
        VALIDATION_CHANGED: 'ui.validation.changed',
        VALIDATION_ALREADY_DECIDED: 'ui.validation.already_decided',
        VALIDATION_WAITING: 'ui.validation.waiting_for_pipelines',
        VALIDATION_REPORT_MAKING: 'ui.validation.report_making',
        VALIDATION_REPORT_FAILED: 'ui.validation.report_failed',
        BOOKING_CLINICIAN_MISSING: 'ui.validation.booking_not_clinician',
        CLINIC_PORTAL_UNAVAILABLE: 'ui.validation.booking_portal_down',
      }[requestError.code];
      setError(errorKey ? tx(errorKey) : errorMessage(requestError));
      setDeciding(null);
      if (['VALIDATION_CHANGED', 'VALIDATION_WAITING', 'VALIDATION_REPORT_MAKING', 'VALIDATION_REPORT_FAILED'].includes(requestError.code)) {
        await resource.refresh();
      }
    } finally {
      setBusy('');
    }
  }

  const decidingDetail = deciding === null ? '' : `${personName(deciding.person.patient)} · ${reviewOutputs(deciding.row).map((output) => output.name).join(', ')}`;
  return (
    <div className="xo-products-page xo-validation-page">
      <Head title={text('title')} subtitle={text('people_subtitle')} />
      <div className="xo-product-navigation">
        <div className="xo-product-toolbar">
          <span className="xo-val-count" role="status">{current ? text(data.total === 1 ? 'people_count_one' : 'people_count', { count: data.total }) : ''}</span>
          <div className="xo-product-toolbar-actions">
            {mayGlobal && <FilterMenu icon={Globe2} label={text('scope')} value={scope} onChange={change(setScope)}
              options={[{ value: 'local', label: text('your_location') }, { value: 'global', label: text('all_locations') }]} />}
            <FilterMenu icon={SlidersHorizontal} label={text('status_filter')} value={status} onChange={change(setStatus)}
              options={STATUSES.map((value) => ({ value, label: text(`status_${value}`), count: data === null || data.scope !== scope ? undefined : data.statusCounts[value] }))} />
          </div>
        </div>
      </div>

      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      {notice && <p className="xo-alert t-ok" role="status">{notice}</p>}

      <section aria-busy={!resource.error && !current}>
        {resource.error ? <Failed message={resource.error} onRetry={resource.refresh} />
          : !current ? <Loading label={text('loading')} />
            : data.people.length === 0 ? <Empty title={text('empty')} detail={text('empty_detail')} />
              : <div className="xo-val-people">{data.people.map((person) => <Person key={person.validations[0].collection.uuid} person={person} now={now}
                busy={busy} onApprove={ask('approved')} onReject={ask('rejected')} onOpenReport={showReport} />)}</div>}
      </section>

      {current && data.total > data.limit && (
        <div className="xo-foot-split">
          <span>{text('showing', { from: offset + 1, to: Math.min(offset + data.people.length, data.total), total: data.total })}</span>
          <div className="xo-bar-group">
            <button type="button" className="xo-btn-2" disabled={offset === 0 || resource.loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>{tx('ui.ui.previous_50f94286')}</button>
            <button type="button" className="xo-btn-2" disabled={offset + data.people.length >= data.total || resource.loading}
              onClick={() => setOffset(offset + PAGE_SIZE)}>{tx('ui.ui.next_bc981983')}</button>
          </div>
        </div>
      )}

      {deciding !== null && deciding.decision === 'approved' && deciding.row.customerSample && <ReleaseDialog key={deciding.row.collection.uuid}
        detail={decidingDetail} busy={busy === deciding.row.collection.uuid} onCancel={() => setDeciding(null)}
        onApprove={(emails) => decide(deciding.row, 'approved', null, emails)} />}
      {deciding !== null && deciding.decision === 'approved' && !deciding.row.customerSample && <ApproveDialog key={deciding.row.collection.uuid} row={deciding.row} detail={decidingDetail}
        busy={busy === deciding.row.collection.uuid} onCancel={() => setDeciding(null)}
        onApprove={(emails) => decide(deciding.row, 'approved', null, emails)} />}
      {deciding !== null && deciding.decision === 'rejected' && <RejectDialog key={deciding.row.collection.uuid} detail={decidingDetail}
        busy={busy === deciding.row.collection.uuid} onCancel={() => setDeciding(null)}
        onReject={(note) => decide(deciding.row, 'rejected', note)} />}
      {openReport !== null && <RequestDetailModal request={openReport.request} options={openReport.options}
        onChanged={(updated) => setOpenReport((current) => ({ ...current, request: updated }))}
        onClose={() => setOpenReport(null)} />}
    </div>
  );
}
